<template>
  <nav class="landing-navbar" :class="{ scrolled: isScrolled }">
    <router-link to="/" class="nav-brand">
      <img src="@/assets/logo.png" alt="Koke Logo" class="nav-logo" />
      <span class="nav-brand-name">KokeManga</span>
    </router-link>

    <div class="nav-links">
      <a href="#features" class="nav-link">{{ $t('nav.features') }}</a>
      <a href="#pricing" class="nav-link">{{ $t('nav.pricing') }}</a>
      <a href="#faq" class="nav-link">{{ $t('nav.faq') }}</a>
      <div class="lang-dropdown" @mouseenter="showLangMenu = true" @mouseleave="showLangMenu = false">
        <div class="lang-dropdown-trigger">
          <i class="fas fa-globe"></i>
          <span>{{ langLabel }}</span>
          <i class="fas fa-chevron-down" :class="{ open: showLangMenu }"></i>
        </div>
        <transition name="fade">
          <div v-if="showLangMenu" class="lang-dropdown-menu">
            <div v-for="lang in langOptions" :key="lang.value" class="lang-dropdown-item"
              :class="{ active: locale === lang.value }" @click="selectLang(lang.value)">
              <span>{{ lang.label }}</span>
              <i v-if="locale === lang.value" class="fas fa-check"></i>
            </div>
          </div>
        </transition>
      </div>
      <button @click="goTranslate" class="nav-btn">
        <i class="fas fa-rocket"></i>
        {{ $t('nav.startBtn') }}
      </button>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { localPath } from '@/i18n'
import { getToken } from '@/utils/auth.js'

const router = useRouter()
const { t, locale } = useI18n()
const isScrolled = ref(false)
const showLangMenu = ref(false)

const langOptions = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh-TW', label: '繁體中文' }
]

const langLabel = computed(() => {
  const found = langOptions.find(l => l.value === locale.value)
  return found ? found.label : '中文'
})

const selectLang = (lang) => {
  locale.value = lang
  localStorage.setItem('lang', lang)
  const path = router.currentRoute.value.fullPath
  const newPath = path.replace(/^\/(zh-TW|zh|en|ja|ko)/, `/${lang}`)
  router.push(newPath)
  showLangMenu.value = false
}

const isLoggedIn = computed(() => {
  const token = getToken()
  return !!(token && token !== undefined)
})

const goTranslate = () => {
  if (isLoggedIn.value) {
    router.push(localPath('/vip'))
  } else {
    router.push(localPath('/MangaTranslation'))
  }
}

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
.landing-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 1rem 5%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
}

.landing-navbar.scrolled {
  background: rgba(10, 15, 26, 0.9);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(94, 234, 212, 0.1);
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.nav-logo {
  height: 2rem;
  filter: drop-shadow(0 2px 8px rgba(94, 234, 212, 0.3));
}

.nav-brand-name {
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-link {
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s;
  position: relative;
}

.nav-link:hover {
  color: #5eead4;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  right: 0;
  height: 2px;
  background: #5eead4;
  transform: scaleX(0);
  transition: transform 0.3s;
}

.nav-link:hover::after {
  transform: scaleX(1);
}

.nav-btn {
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s;
  background: linear-gradient(135deg, #5eead4, #06b6d4);
  color: #0a0f1a;
}

.nav-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(94, 234, 212, 0.3);
}

.lang-dropdown {
  position: relative;
}

.lang-dropdown-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 8px;
  background: rgba(94, 234, 212, 0.08);
  border: 1px solid rgba(94, 234, 212, 0.15);
  color: #e2e8f0;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  user-select: none;
  line-height: 1;
}

.lang-dropdown-trigger .fa-globe {
  font-size: 0.9rem;
  color: #5eead4;
  vertical-align: middle;
  transform: translateY(1px);
}

.lang-dropdown-trigger .fa-chevron-down {
  font-size: 0.55rem;
  color: #5eead4;
  margin-left: 2px;
  transition: transform 0.3s ease;
  vertical-align: middle;
  transform: translateY(1px);
}

.lang-dropdown-trigger .fa-chevron-down.open {
  transform: translateY(1px) rotate(180deg);
}

.lang-dropdown-trigger:hover {
  background: rgba(94, 234, 212, 0.15);
  border-color: rgba(94, 234, 212, 0.3);
  box-shadow: 0 0 12px rgba(94, 234, 212, 0.1);
}

.lang-dropdown-trigger i:first-child {
  color: #5eead4;
  font-size: 0.85rem;
}

.lang-dropdown-trigger .fa-chevron-down {
  font-size: 0.6rem;
  color: #5eead4;
  transition: transform 0.3s ease;
  margin-left: 2px;
}

.lang-dropdown-trigger .fa-chevron-down.open {
  transform: rotate(180deg);
}

.lang-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 140px;
  background: rgba(15, 23, 42, 0.95);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 10px;
  padding: 6px;
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 20px rgba(94, 234, 212, 0.05);
  z-index: 100;
}

.lang-dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 6px;
  color: #94a3b8;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.lang-dropdown-item:hover {
  background: rgba(94, 234, 212, 0.1);
  color: #e2e8f0;
}

.lang-dropdown-item.active {
  color: #5eead4;
  background: rgba(94, 234, 212, 0.08);
}

.lang-dropdown-item .fa-check {
  font-size: 0.7rem;
  color: #5eead4;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-4px);
}

@media (max-width: 768px) {
  .nav-links {
    display: none;
  }
}
</style>
